'use client';

import { Dropdown } from '@benzinga/core-ui';
import { BenzingaLogo } from '@benzinga/logos-ui';
import { getPremiumServiceHref, type PremiumServiceAccess, type PremiumServiceKey } from '../../premiumServiceLinks';
import styles from './SiteNav.module.scss';

interface PremiumServiceItem {
  key: PremiumServiceKey;
  product: string;
  description: string;
}

const PREMIUM_SERVICES: PremiumServiceItem[] = [
  { key: 'edge', product: 'Edge', description: 'Ratings, signals & screens' },
  { key: 'pro', product: 'Pro', description: 'Real-time news & data terminal' },
  { key: 'research', product: 'Research', description: 'Trade alerts & newsletters' },
  { key: 'apis', product: 'APIs', description: 'Market data licensing' },
];

export interface PremiumServicesDropdownProps {
  access: PremiumServiceAccess;
  label?: string;
  variant?: 'default' | 'dark';
  onLinkClick?: (service: PremiumServiceKey) => void;
}

export const PremiumServicesDropdown = ({
  access,
  label = 'Premium',
  variant = 'default',
  onLinkClick,
}: PremiumServicesDropdownProps) => {
  const logoVariant = variant === 'dark' ? 'light' : 'dark';
  return (
    <Dropdown
      trigger="hover"
      placement="bottom-end"
      distance={8}
      headless
      triggerClassName="rounded-lg"
      target={
        <a
          className={
            styles.premiumTrigger +
            " relative inline-flex h-[38px] cursor-pointer appearance-none items-center whitespace-nowrap rounded-lg border-0 bg-[linear-gradient(180deg,#3b8cff_0%,#1a79ff_45%,#0b57d0_100%)] px-4 font-[inherit] text-sm font-bold tracking-[0.01em] text-white no-underline [text-shadow:0_1px_1px_rgba(0,0,0,0.18)] transition-[filter] duration-200 ease-out after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:bg-[linear-gradient(110deg,transparent_35%,rgba(255,255,255,0.35)_50%,transparent_65%)] after:content-[''] after:-translate-x-full hover:brightness-105 hover:after:translate-x-full hover:after:transition-transform hover:after:duration-700 motion-reduce:hover:after:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--hubnav-accent)]"
          }
          style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.28)' }}
          href={getPremiumServiceHref('services', access)}
          aria-label="Premium Services"
          onClick={() => onLinkClick?.('services')}
        >
          {label}
        </a>
      }
    >
      <ul
        className="m-0 min-w-[280px] list-none rounded-xl border border-[var(--hubnav-line)] bg-[var(--hubnav-panel-bg)] p-1.5 shadow-[var(--hubnav-shadow)]"
        role="menu"
        aria-label="Premium Services"
      >
        {PREMIUM_SERVICES.map(service => (
          <li key={service.key}>
            <a
              className="flex flex-col gap-0.5 rounded-lg px-3 py-2.5 no-underline hover:bg-[var(--hubnav-hover)] hover:[&_[data-product=edge]]:text-[#3f83f8] hover:[&_[data-product=pro]]:text-[#1a79ff] hover:[&_[data-product=research]]:text-[#ff5a1f] hover:[&_[data-product=apis]]:text-[#e0474c]"
              href={getPremiumServiceHref(service.key, access)}
              aria-label={`Benzinga ${service.product}`}
              onClick={() => onLinkClick?.(service.key)}
            >
              <span className="inline-flex flex-none items-center gap-1.5 self-start [&_.benzinga-logo]:h-3 [&_.benzinga-logo]:w-auto [&_.benzinga-logo]:min-w-0 [&_.benzinga-logo]:max-w-none [&_.benzinga-logo]:flex-none [&_.benzinga-logo_svg]:h-3 [&_.benzinga-logo_svg]:w-auto">
                <BenzingaLogo variant={logoVariant} />
                <span
                  className="text-sm font-bold text-[var(--hubnav-fg)] transition-colors duration-150 ease-out"
                  data-product={service.key}
                >
                  {service.product}
                </span>
              </span>
              <span className="text-xs text-[var(--hubnav-muted)]">{service.description}</span>
            </a>
          </li>
        ))}
      </ul>
    </Dropdown>
  );
};
